import { useSyncExternalStore } from 'react'
import { platform as currentPlatform } from './index'
import type { IPlatform } from './types'

/**
 * Desktop-app title bar (apps/desktop). On macOS the shell hides the title bar
 * and draws the window buttons over the page's top-left corner, where the nav
 * rail's Edge mark sits. These helpers tell the page when that is the case, so
 * the rail can make room and each page's top row can move the window.
 *
 * Full screen counts as "not inset": macOS hides the window buttons there.
 */

function isInset(p: IPlatform): boolean {
  return p.titleBar === 'inset' && !p.isFullScreen()
}

/**
 * Keeps `data-title-bar="inset"` on `root` while the window buttons cover the
 * page. CSS keys the window drag areas off it (styles/window-chrome.css).
 * Returns a cleanup that removes the mark and unsubscribes.
 */
export function syncTitleBarAttribute(
  p: IPlatform = currentPlatform,
  root: HTMLElement = document.documentElement,
): () => void {
  if (p.titleBar !== 'inset') return () => {}
  const apply = () => {
    if (isInset(p)) root.dataset.titleBar = 'inset'
    else delete root.dataset.titleBar
  }
  apply()
  const unsubscribe = p.onFullScreenChange(apply)
  return () => {
    unsubscribe()
    delete root.dataset.titleBar
  }
}

/** True while the desktop window buttons sit over the page's top-left corner. */
export function useTitleBarInset(p: IPlatform = currentPlatform): boolean {
  return useSyncExternalStore(
    (onChange) => p.onFullScreenChange(onChange),
    () => isInset(p),
  )
}
